import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import globalAxios from "../../globalAxios";

import {
	Box,
	Typography,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Paper,
	Skeleton,
} from "@mui/material";

import IconButton from "@mui/material/IconButton";

import PersonIcon from "@mui/icons-material/Person";
import MedicalServicesIcon from "@mui/icons-material/MedicalServices";
import ListAltIcon from "@mui/icons-material/ListAlt";

import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";

import { calculateEndTime } from "../calendar/helper/eventRendering.tsx";
import { setSelectedSession } from "../../store/slices/sessionSlice.ts";
import {
	setCalendarDate,
	setCalendarView,
	setSelectedEntityType,
} from "../../store/slices/calendarSlice.ts";
import { useAppDispatch } from "../../store/hooks.ts";
import { setSelectedEvent } from "../../store/slices/eventSlice.ts";
import {
	setLoadingDetail,
	setSelectedItem,
} from "../../store/slices/searchSlice.ts";
import { SERVER_URL } from "../../config.js";

const DatabaseView = () => {
	const navigate = useNavigate();
	const dispatch = useAppDispatch();

	const {
		recentPatients,
		recentSessions,
		recentEvents,
		recentSurgeons,
		loading,
		error,
	} = useRecentEntities();

	if (loading) {
		return (
			<>
				<Typography variant="h5" gutterBottom>
					My Database
				</Typography>
				<Box
					sx={{
						display: "flex",
						alignItems: "center",
						gap: 1,
						mt: 3,
					}}
				>
					<PersonIcon sx={{ mb: "0.5rem" }} fontSize="small" />
					<Typography variant="h6" gutterBottom>
						Recent Patients
					</Typography>
				</Box>
				<SkeletonTable columns={2} />

				<Box
					sx={{
						display: "flex",
						alignItems: "center",
						gap: 1,
						mt: 3,
					}}
				>
					<ListAltIcon sx={{ mb: "0.5rem" }} fontSize="small" />
					<Typography variant="h6" gutterBottom>
						Recent Sessions
					</Typography>
				</Box>
				<SkeletonTable columns={3} />

				<Box
					sx={{
						display: "flex",
						alignItems: "center",
						gap: 1,
						mt: 3,
					}}
				>
					<MedicalServicesIcon
						sx={{ mb: "0.5rem" }}
						fontSize="small"
					/>
					<Typography variant="h6" gutterBottom>
						Recent Events
					</Typography>
				</Box>
				<SkeletonTable columns={3} />

				<Box
					sx={{
						display: "flex",
						alignItems: "center",
						gap: 1,
						mt: 3,
					}}
				>
					<PersonIcon sx={{ mb: "0.5rem" }} fontSize="small" />
					<Typography variant="h6" gutterBottom>
						My Surgeons
					</Typography>
				</Box>
				<SkeletonTable columns={3} />
			</>
		);
	}

	if (error) {
		return (
			<Box sx={{ mt: 4 }}>
				<Typography color="error">Failed to load data.</Typography>
			</Box>
		);
	}

	const handleDatabaseNavigate = async (item, type) => {
		console.log();
		const dateStr = item.date_of_service;
		const startTime = item.start || "08:00";
		const startDateTime = `${dateStr}T${startTime}:00`;

		if (type === "session") {
			const endDateTime = calculateEndTime(dateStr, startTime, null);
			console.log("session ", item);
			dispatch(
				setSelectedSession({
					title: item.hospital?.name || "Session",
					id: item.id,
					start: startDateTime,
					end: endDateTime,
					surgeon: {
						...item.surgeon,
						name: `${item.surgeon?.first_name || ""} ${
							item.surgeon?.last_name || ""
						}`,
					},
					hospital: item.hospital,
					events: item.events,
				})
			);
			dispatch(setCalendarDate(dateStr));
			dispatch(setCalendarView("dayGridMonth"));
			dispatch(setSelectedEntityType("session"));
			navigate("/calendar");
		} else if (type === "event") {
			const session = item.session;
			if (session) {
				const sessionStartTime = session.start || "08:00";
				const sessionStart = `${session.date_of_service}T${sessionStartTime}:00`;
				const sessionEnd = calculateEndTime(
					session.date_of_service,
					sessionStartTime,
					null
				);

				dispatch(
					setSelectedSession({
						title: session.hospital?.name || "Session",
						id: session.id,
						start: sessionStart,
						end: sessionEnd,
						surgeon: {
							...session.surgeon,
							name: `${session.surgeon?.first_name || ""} ${
								session.surgeon?.last_name || ""
							}`,
						},
						hospital: session.hospital,
						events: session.events,
					})
				);
			}
			dispatch(setSelectedEvent(item));
			dispatch(setCalendarDate(dateStr));
			dispatch(setCalendarView("dayGridMonth"));
			dispatch(setSelectedEntityType("event"));
			navigate("/calendar");
		} else if (type === "patient") {
			item.type = "patient";

			if (!item.id) return;
			dispatch(setLoadingDetail(true));
			dispatch(setSelectedItem(item));
			try {
				const res = await globalAxios.get(
					`${SERVER_URL}/search/patient/${item.id}`
				);
				dispatch(setSelectedItem({ ...res.data, type: "patient" }));
				dispatch(setLoadingDetail(false));
			} catch (err) {
				console.error(
					"Failed to fetch patient with linked events",
					err
				);
			}
		}
	};

	return (
		<Box sx={{ mt: 4, maxWidth: 1000 }}>
			<Typography variant="h5" gutterBottom>
				My Database
			</Typography>

			{/* Patients */}
			{recentPatients.length > 0 && (
				<>
					<Box
						sx={{
							display: "flex",
							alignItems: "center",
							gap: 1,
							mt: 3,
						}}
					>
						<PersonIcon sx={{ mb: "0.5rem" }} fontSize="small" />
						<Typography variant="h6" gutterBottom>
							Recent Patients
						</Typography>
					</Box>

					<TableContainer component={Paper} sx={{ mb: 2 }}>
						<Table size="small">
							<TableHead sx={{ backgroundColor: "grey.100" }}>
								<TableRow>
									<TableCell>Name</TableCell>
									<TableCell>D.O.B.</TableCell>
									<TableCell>Email</TableCell>
									<TableCell></TableCell>
								</TableRow>
							</TableHead>
							<TableBody>
								{recentPatients.map((p) => (
									<TableRow key={p.id}>
										<TableCell>
											{p.first_name + " " + p.last_name}
										</TableCell>
										<TableCell>{p.date_of_birth}</TableCell>
										<TableCell>{p.email}</TableCell>
										<TableCell align="right">
											<IconButton
												sx={{ padding: 0 }}
												onClick={() =>
													handleDatabaseNavigate(
														p,
														"patient"
													)
												}
											>
												<ArrowForwardIosIcon fontSize="small" />
											</IconButton>
										</TableCell>
									</TableRow>
								))}
							</TableBody>
						</Table>
					</TableContainer>
				</>
			)}

			{/* Sessions */}
			{recentSessions.length > 0 && (
				<>
					<Box
						sx={{
							display: "flex",
							alignItems: "center",
							gap: 1,
							mt: 3,
						}}
					>
						<ListAltIcon sx={{ mb: "0.5rem" }} fontSize="small" />
						<Typography variant="h6" gutterBottom>
							Recent Sessions
						</Typography>
					</Box>
					<TableContainer component={Paper} sx={{ mb: 2 }}>
						<Table size="small">
							<TableHead sx={{ backgroundColor: "grey.100" }}>
								<TableRow>
									<TableCell>Date</TableCell>
									<TableCell>Surgeon</TableCell>
									<TableCell>Hospital</TableCell>
									<TableCell></TableCell>
								</TableRow>
							</TableHead>
							<TableBody>
								{recentSessions.map((s) => (
									<TableRow key={s.id}>
										<TableCell>
											{new Date(
												s.date_of_service
											).toLocaleDateString()}
										</TableCell>
										<TableCell>
											{s.surgeon?.first_name}{" "}
											{s.surgeon?.last_name}
										</TableCell>
										<TableCell>
											{s.hospital?.name}
										</TableCell>
										<TableCell align="right">
											<IconButton
												sx={{ padding: 0 }}
												onClick={() =>
													handleDatabaseNavigate(
														s,
														"session"
													)
												}
											>
												<ArrowForwardIosIcon fontSize="small" />
											</IconButton>
										</TableCell>
									</TableRow>
								))}
							</TableBody>
						</Table>
					</TableContainer>
				</>
			)}

			{/* Events */}
			{recentEvents.length > 0 && (
				<>
					<Box
						sx={{
							display: "flex",
							alignItems: "center",
							gap: 1,
							mt: 3,
						}}
					>
						<MedicalServicesIcon
							sx={{ mb: "0.5rem" }}
							fontSize="small"
						/>
						<Typography variant="h6" gutterBottom>
							Recent Events
						</Typography>
					</Box>
					<TableContainer component={Paper}>
						<Table size="small">
							<TableHead sx={{ backgroundColor: "grey.100" }}>
								<TableRow>
									<TableCell>Procedure</TableCell>
									<TableCell>Patient</TableCell>
									<TableCell>Date</TableCell>
									<TableCell></TableCell>
								</TableRow>
							</TableHead>
							<TableBody>
								{recentEvents.map((e) => (
									<TableRow key={e.id}>
										<TableCell>{e.procedure}</TableCell>
										<TableCell>
											{e.patient?.first_name}{" "}
											{e.patient?.last_name}
										</TableCell>
										<TableCell>
											{new Date(
												e.date_of_service
											).toLocaleDateString()}
										</TableCell>
										<TableCell align="right">
											<IconButton
												sx={{ padding: 0 }}
												onClick={() =>
													handleDatabaseNavigate(
														e,
														"event"
													)
												}
											>
												<ArrowForwardIosIcon fontSize="small" />
											</IconButton>
										</TableCell>
									</TableRow>
								))}
							</TableBody>
						</Table>
					</TableContainer>
				</>
			)}

			{recentSurgeons.length > 0 && (
				<>
					<Box
						sx={{
							display: "flex",
							alignItems: "center",
							gap: 1,
							mt: 3,
						}}
					>
						<PersonIcon sx={{ mb: "0.5rem" }} fontSize="small" />
						<Typography variant="h6" gutterBottom>
							My Surgeons
						</Typography>
						<Typography
							variant="body2"
							sx={{
								cursor: "pointer",
								color: "primary.main",
								ml: 1,
							}}
							onClick={() => navigate("/surgeons")}
						>
							See all
						</Typography>
					</Box>

					<TableContainer component={Paper} sx={{ mb: 2 }}>
						<Table size="small">
							<TableHead sx={{ backgroundColor: "grey.100" }}>
								<TableRow>
									<TableCell>Name</TableCell>
									<TableCell></TableCell>
								</TableRow>
							</TableHead>
							<TableBody>
								{recentSurgeons.map((s) => (
									<TableRow key={s.id}>
										<TableCell>
											{(s.first_name || "") +
												" " +
												(s.last_name || "")}
										</TableCell>
										<TableCell align="right">
											<IconButton
												sx={{ padding: 0 }}
												onClick={() =>
													handleDatabaseNavigate(
														s,
														"surgeon"
													)
												}
											>
												<ArrowForwardIosIcon fontSize="small" />
											</IconButton>
										</TableCell>
									</TableRow>
								))}
							</TableBody>
						</Table>
					</TableContainer>
				</>
			)}
		</Box>
	);
};

export default DatabaseView;

const useRecentEntities = () => {
	const [recentPatients, setRecentPatients] = useState([]);
	const [recentSessions, setRecentSessions] = useState([]);
	const [recentEvents, setRecentEvents] = useState([]);
	const [recentSurgeons, setRecentSurgeons] = useState([]);

	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(null);

	useEffect(() => {
		const fetchRecents = async () => {
			try {
				setLoading(true);
				const res = await globalAxios.get("/user/recents");
				const { patients, sessions, events, surgeons } = res.data;

				setRecentPatients(patients);
				setRecentSessions(sessions);
				setRecentEvents(events);
				setRecentSurgeons(surgeons);
			} catch (err) {
				console.error("Failed to fetch recents:", err);
				setError(err);
			} finally {
				setLoading(false);
			}
		};

		fetchRecents();
	}, []);

	return {
		recentPatients,
		recentSessions,
		recentEvents,
		recentSurgeons,
		loading,
		error,
	};
};

const SkeletonTable = ({ columns = 3, rows = 5 }) => (
	<TableContainer component={Paper} sx={{ mb: 2 }}>
		<Table size="small">
			<TableHead>
				<TableRow>
					{Array.from({ length: columns }).map((_, idx) => (
						<TableCell key={idx}>
							<Skeleton variant="text" width="80%" />
						</TableCell>
					))}
				</TableRow>
			</TableHead>
			<TableBody>
				{Array.from({ length: rows }).map((_, rowIdx) => (
					<TableRow key={rowIdx}>
						{Array.from({ length: columns }).map((_, colIdx) => (
							<TableCell key={colIdx}>
								<Skeleton variant="text" width="90%" />
							</TableCell>
						))}
					</TableRow>
				))}
			</TableBody>
		</Table>
	</TableContainer>
);
